/**
 * Admin FinOps & Audit Log API
 *
 * Requires admin session token (X-Session-Token with admin privileges).
 * Calls /admin/finops/* and /admin/audit/log endpoints.
 */

import api from "./index";
import ENDPOINTS from "./endpoints";
import logger from "../services/Logger";

/**
 * Fetch the aggregated org-wide usage summary for the FinOps dashboard.
 * @param {Object} [opts]
 * @param {string} [opts.period] - "7d" | "30d" | "90d" | "12m" | "Custom"
 * @param {string} [opts.from] - ISO date (required when period="Custom")
 * @param {string} [opts.to] - ISO date (required when period="Custom")
 * @returns {Promise<Object>} { totals, by_model, by_request_type, users, daily }
 */
export const getFinOpsSummary = async ({ period = "30d", from, to } = {}) => {
  try {
    const params = { period };
    if (period === "Custom") {
      if (from) params.from = from;
      if (to) params.to = to;
    }
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_FINOPS_SUMMARY,
      params,
    });
    return response.data;
  } catch (error) {
    logger.error("adminFinOps", "Error fetching FinOps summary", { error: error.message });
    throw error;
  }
};

/**
 * Fetch the organisation's audit events (most recent first), paginated by cursor.
 * @param {Object} [opts]
 * @param {number} [opts.limit]      - max events per page (default 100)
 * @param {string} [opts.period]     - "7d" | "30d" | "90d" | "custom"
 * @param {string} [opts.from]       - ISO date string (required when period=custom)
 * @param {string} [opts.to]         - ISO date string (required when period=custom)
 * @param {string} [opts.eventType]  - optional server-side event_type filter
 * @param {string} [opts.entityId]   - optional entity_id filter
 * @param {string} [opts.cursor]     - pagination cursor from previous response
 * @returns {Promise<{events: Array, next_cursor: string|null}>}
 */
export const getAuditLog = async ({ limit = 100, period, from: fromDate, to, eventType, entityId, cursor } = {}) => {
  try {
    const params = { limit };
    if (period)    params.period     = period;
    if (fromDate)  params.from       = fromDate;
    if (to)        params.to         = to;
    if (eventType) params.event_type = eventType;
    if (entityId)  params.entity_id  = entityId;
    if (cursor)    params.cursor     = cursor;
    const response = await api({
      method: "GET",
      url: ENDPOINTS.ADMIN_AUDIT_LOG,
      params,
    });
    return response.data;  // { events, next_cursor }
  } catch (error) {
    logger.error("adminFinOps", "Error fetching audit log", { error: error.message });
    throw error;
  }
};
